import type { Contract } from 'bundles/admin-dashboard/components/EnterpriseAdminContext';
import { org_coursera_enterprise_contract_tag_EnterpriseContractTag as ContractTag } from 'bundles/admin-dashboard/utils/__generated__/globalTypes';
import type {
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements_curriculum_ProgramsV1_tracksCurriculumMember_tracksCurriculum_coreTracks as CoreTracks,
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements_curriculum_ProgramsV1_tracksCurriculumMember_tracksCurriculum_electiveTracks_curriculumItems_ProgramsV1_courseIdMember as CurriculumCourse,
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements_curriculum_ProgramsV1_tracksCurriculumMember_tracksCurriculum_electiveTracks as ElectiveTracks,
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements as Program,
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements_curriculum_ProgramsV1_tracksCurriculumMember as TracksCurriculum,
  ProgramsByOrgIdQuery_ProgramsV1Resource_byThirdPartyOrganizationId_elements_curriculum_ProgramsV1_wildcardCurriculumMember as WildCardCurriculum,
} from 'bundles/enterprise-admin-custom-courses/queries/__generated__/ProgramsByOrgIdQuery';

// filter out Career Academy and Academic Discipline collections
const getFilteredCollectionsHelper = (collections: (CoreTracks | ElectiveTracks | null)[] | undefined) => {
  if (!collections) {
    return [];
  }
  const filteredCollections = collections.filter((collection) => {
    if (!collection || !collection.title) {
      return false;
    }

    const isCareerAcademyCollection = collection.title.startsWith('Career Academy:') && collection.isLocked;
    const isAcademicDisciplineCollection = collection.subjectId;

    return !isCareerAcademyCollection && !isAcademicDisciplineCollection;
  });

  return filteredCollections;
};

export const getFilteredCollections = (
  selectedProgramId: string | undefined,
  programs: (Program | null)[] | undefined
) => {
  if (!selectedProgramId || !programs) return undefined;

  const selectedProgram = programs?.find((program) => program?.id === selectedProgramId);
  if (!selectedProgram || !selectedProgram.curriculum) return undefined;
  const isTracksCurriculum = !!(selectedProgram?.curriculum as TracksCurriculum).tracksCurriculum;

  const collections = isTracksCurriculum
    ? [
        ...(selectedProgram?.curriculum as TracksCurriculum)?.tracksCurriculum.coreTracks,
        ...(selectedProgram?.curriculum as TracksCurriculum)?.tracksCurriculum.electiveTracks,
      ]
    : [...(selectedProgram?.curriculum as WildCardCurriculum)?.wildcardCurriculum.electiveTracks];

  return getFilteredCollectionsHelper(collections);
};

export const collectionContainsCourse = (
  collections: (CoreTracks | ElectiveTracks | null)[] | undefined,
  selectedCurriculumId: string | undefined,
  courseId: string
) => {
  const selectedCurriculum = collections?.find((collection) => collection?.id === selectedCurriculumId);

  if (!selectedCurriculum) return false;

  return Boolean(
    selectedCurriculum.curriculumItems.find((curriculumItem) => {
      const isCurriculumCourse = !!(curriculumItem as CurriculumCourse).courseId;
      if (!isCurriculumCourse) return false;
      const item = curriculumItem as CurriculumCourse;
      return item.courseId.id === courseId;
    })
  );
};

export const noPrivateAuthoringCanUseCustomCourses = (
  isOrgAdmin: boolean,
  isStandardContractUser: boolean,
  contracts?: Contract[] | null
) => {
  const activeContracts = contracts?.filter((_) => _.contractState === 'ACTIVE');
  const unsupportedTags = [
    ContractTag.CAREER_ACADEMY_LIMITED,
    ContractTag.GROW_WITH_GOOGLE,
    ContractTag.GWG_CC,
    ContractTag.GWG_ENTERPRISE,
    ContractTag.GWG_SMB,
    ContractTag.GWG_E2E,
  ];
  if (
    !Array.isArray(contracts) ||
    activeContracts?.every(
      (contract) =>
        contract.contractTag?.contractTagName && unsupportedTags.includes(contract.contractTag?.contractTagName)
    )
  ) {
    return false;
  }

  return isOrgAdmin && isStandardContractUser;
};
